import type { FormikErrors } from 'formik'
import type { ENTITY_TYPES, IEmailCollectionItem } from '@sales-center/common'
import { isValidPhone } from '@sales-center/common'
import { validateEmailCollection } from '@user/components/EmailCollection'
import { locationUnder30PP } from '@user/components/Settings/EntityFormSettings'

export interface IUpdateContactFormModel {
  address1: string
  address2: string
  altPhoneExt: string
  altPhoneNumber: string
  city: string
  company: string
  contactMethod: string
  contactTime: string[]
  contactType: string
  emailAddresses: IEmailCollectionItem[]
  entityId: number | undefined
  entityType: ENTITY_TYPES | undefined
  firstName: string
  id?: number
  isPrimaryContact: boolean
  lastName: string
  leadId?: number
  opportunityId?: number
  phoneExt: string
  phoneNumber: string
  state: string
  postalCode: string
  county: string
  timeZone: string
}

export const initialValues: IUpdateContactFormModel = {
  id: undefined,
  contactType: '',
  emailAddresses: [
    {
      emailAddress: '',
      isPrimary: true,
    },
  ],
  firstName: '',
  lastName: '',
  company: '',
  phoneNumber: '',
  phoneExt: '',
  altPhoneNumber: '',
  altPhoneExt: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  county: '',
  timeZone: 'Eastern Standard Time',
  contactMethod: '',
  contactTime: [],
  entityId: undefined,
  entityType: undefined,
  isPrimaryContact: false,
}

export function validate(
  model: IUpdateContactFormModel,
  validatePostalCode: (postalCode: string) => string | undefined,
) {
  const errors: FormikErrors<IUpdateContactFormModel> = {}
  const {
    altPhoneNumber,
    altPhoneExt,
    company,
    emailAddresses,
    firstName,
    lastName,
    phoneNumber,
    phoneExt,
    postalCode,
  } = model

  const requireName = company === ''
  const requireCompany = firstName === '' && lastName === ''

  if (requireName) {
    if (firstName === '') errors.firstName = 'Required'
    if (lastName === '') errors.lastName = 'Required'
  }

  if (requireCompany) {
    if (company === '') errors.company = 'Required'
  }

  const hasEmail = emailAddresses.find((x) => x.emailAddress !== '') ?? false

  const requireEmail = phoneNumber === '' && !hasEmail
  const requirePhone = !hasEmail

  const emailErrors = validateEmailCollection(
    emailAddresses,
    requireEmail,
    false,
    true,
  )
  if (emailErrors || requireEmail) errors.emailAddresses = emailErrors

  if (phoneNumber.length === 0) {
    if (requirePhone) errors.phoneNumber = 'Required'
  } else if (!isValidPhone(phoneNumber)) {
    errors.phoneNumber = 'Must be a valid phone number'
  }
  if (altPhoneNumber && !isValidPhone(altPhoneNumber)) {
    errors.altPhoneNumber = 'Must be a valid phone number'
  }

  if (phoneNumber.trim() !== '' && altPhoneNumber.trim() !== '') {
    if (phoneNumber?.trim() === altPhoneNumber?.trim()) {
      errors.altPhoneNumber = 'Must be different from primary phone'
    }
  }

  const numbers = /^[0-9]+$/
  if (phoneExt && phoneExt.trim().length > 0) {
    if (!phoneExt.match(numbers)) {
      errors.phoneExt = 'Please enter a valid phone extension'
    }
    if (phoneExt?.length > 5) {
      errors.phoneExt = 'Phone extension must be between 1 and 5 characters'
    }
  }
  if (altPhoneExt != '') {
    if (!altPhoneExt.match(numbers)) {
      errors.altPhoneExt = 'Please enter a valid alternate phone extension'
    }
    if (altPhoneExt?.length > 5) {
      errors.altPhoneExt = 'Phone extension must be between 1 and 5 characters'
    }
  }

  if (!!firstName && firstName.length > 15) {
    errors.firstName = 'Max length is 15 characters'
  }
  if (!!lastName && lastName.length > 20) {
    errors.lastName = 'Max length is 20 characters'
  }
  if (!!company && company.length > 35) {
    errors.company = 'Max length is 35 characters'
  }
  if (!!model.address1 && model.address1.length > 30) {
    errors.address1 = locationUnder30PP
  }
  if (!!model.address2 && model.address2.length > 30) {
    errors.address2 = locationUnder30PP
  }
  if (postalCode.trim() !== '') {
    const errorPostal = validatePostalCode(postalCode)
    if (errorPostal) errors.postalCode = errorPostal
  }
  return errors
}
